import { Injectable, OnDestroy } from '@angular/core';
import { BackgroundGeolocationPlugin } from '@capacitor-community/background-geolocation';
import { Capacitor, CapacitorHttp, HttpResponse } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import * as dayjs from 'dayjs';
import { distinctUntilChanged, Subject, Subscription, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GlobalConstants } from '../constant/constant';
import { AppEventType } from '../enums/app-events.enum';
import { LOCAL_NOTIFICATION_ACTION_TYPE } from '../enums/notification/notification.enum';
import { LogUserPositionResponse } from '../models/log-user-position/log-user-position-response-interface';
import { AppUserClockStateService } from './app-user-clock-state.service';
import { DeviceInfoService } from './device-info.service';
import { EventBusService } from './event-bus.service';
import { LocationService } from './location.service';
import { NotificationsService } from './notifications.service';

@Injectable({
  providedIn: 'root',
})
export class LocationTrackingService implements OnDestroy {
  private readonly isNativePlatform = Capacitor.isNativePlatform();
  locationWatcherId: string;
  geoLocationPlugin: BackgroundGeolocationPlugin;

  private destroy$ = new Subject<void>();
  private subscriptions: Subscription[] = [];
  private locationSessionId = 0;

  constructor(
    private deviceInfo: DeviceInfoService, 
    private notificationsService: NotificationsService, 
    private eventBusService: EventBusService,
    private locationService: LocationService,
    private appUserClockStateService: AppUserClockStateService
  ) {
    this.geoLocationPlugin = Capacitor.registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation');
    this.loadSubscriptions();
  }

  ngOnDestroy(): void {
    this.locationSessionId++;
    this.locationService.clearLastKnownLocation();
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  async loadSubscriptions() {
    const retrySubscription = this.eventBusService.on(AppEventType.RETRY_LOCATION_REQUESTED_BY_USER, () => this.refetchUserCurrentLocation());
    const sessionSubscription = this.eventBusService.on([AppEventType.LOGIN_SUCCESS, AppEventType.LOGOUT_SUCCESS], () => {
      this.locationSessionId++;
      this.locationService.clearLastKnownLocation();
    });
    this.subscriptions.push(retrySubscription, sessionSubscription);
    
    // Start tracking location if user is a clock time entry user and is currently working (not on break)
    // If the user is not a clock time entry user, location is not tracked
    this.appUserClockStateService.isWorking$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged()
      )
      .subscribe(shouldTrack =>
        shouldTrack ? this.startLocationWatcher() : this.stopLocationWatcher()
      );
  }

  async startLocationWatcher() {
    const sessionId = this.locationSessionId;
    await this.stopLocationWatcher();
    if (sessionId !== this.locationSessionId) {
      return;
    }

    if (this.isNativePlatform) {
      await this.startAppLocationWatcher();
    } else {
      const permissions = await Geolocation.checkPermissions();
      if (sessionId !== this.locationSessionId) {
        return;
      }
      if (permissions.location === 'granted') {
        await this.startWebLocationWatcher();
      } else {
        const permission = await Geolocation.requestPermissions();
        if (sessionId === this.locationSessionId && permission.location === 'granted') {
          await this.startWebLocationWatcher();
        } else {
          return;
        }
      }
    }
  }

  async stopLocationWatcher() {
    if (this.locationWatcherId) {
      if (this.isNativePlatform) {
        await this.geoLocationPlugin.removeWatcher({ id: this.locationWatcherId });
      } else {
        await Geolocation.clearWatch({ id: this.locationWatcherId });
      }
      this.eventBusService.emit({ type: AppEventType.USER_CURRENT_POSITION_LOGGED, data:null });
    }
  }

  async refetchUserCurrentLocation(){
    const inputType = await this.deviceInfo.getInputType();
    const currentPosition = await this.locationService.getCurrentLocation();
    const location = {
      PositionTimestamp: dayjs().format(),
      Latitude: currentPosition?.coords?.latitude,
      Longitude: currentPosition?.coords?.longitude,
      InputType: inputType,
    };

    await this.uploadCurrentLocation(location);
  }

  private async startAppLocationWatcher() {
    const sessionId = this.locationSessionId;
    let timestamp: number = 0;
    const inputType = await this.deviceInfo.getInputType();
    if (sessionId !== this.locationSessionId) {
      return;
    }
    this.locationWatcherId = await this.geoLocationPlugin.addWatcher(
      {
        backgroundMessage: 'Your location is recorded in the background while you are clocked in.',
        backgroundTitle: 'You are Clocked In',
        requestPermissions: true,
        distanceFilter: 1,
      },
      async (position, error) => {
        if (sessionId !== this.locationSessionId) {
          return;
        }
        if (error) {
          if (error.code === 'NOT_AUTHORIZED') {
            this.geoLocationPlugin.openSettings();
          }
          return;
        }
        if (position) {
          this.locationService.rememberCurrentLocation({
            timestamp: position.time,
            coords: {
              latitude: position.latitude,
              longitude: position.longitude,
              accuracy: position.accuracy,
              altitude: position.altitude,
              altitudeAccuracy: position.altitudeAccuracy,
              heading: position.bearing,
              speed: position.speed,
            },
          });
        }
        if (position && position.latitude && position.longitude) {
          const networkStatus = await this.deviceInfo.getNetworkStatus();
          if (sessionId !== this.locationSessionId || !networkStatus.connected) {
            return;
          }
          const currentTimestamp = position.time;
          if (currentTimestamp >= timestamp + GlobalConstants.GPS_TRACKING_POLLING_INTERVAL) {
            timestamp = currentTimestamp;
            await this.uploadCurrentLocation({
              PositionTimestamp: dayjs().format(),
              Latitude: position.latitude,
              Longitude: position.longitude,
              InputType: inputType,
            });
          }
        }
      }
    );
  }

  private async startWebLocationWatcher() {
    const sessionId = this.locationSessionId;
    let timestamp: number = 0;
    const inputType = await this.deviceInfo.getInputType();
    if (sessionId !== this.locationSessionId) {
      return;
    }
    this.notificationsService.scheduleLocalNotification({
      id: LOCAL_NOTIFICATION_ACTION_TYPE.LOCATION_TRACKING,
      title: 'Location Tracking',
      body: 'Your location is recorded in the background while you are clocked in.',
      schedule: { at: new Date(Date.now() + 1000) },
    });

    this.locationWatcherId = await Geolocation.watchPosition({ timeout: GlobalConstants.GPS_TRACKING_MAX_TIMEOUT, enableHighAccuracy: true }, async position => {
      if (sessionId !== this.locationSessionId) {
        return;
      }
      if (position) {
        this.locationService.rememberCurrentLocation(position);
      }
      if (position && position.coords.latitude && position.coords.longitude) {
        const networkStatus = await this.deviceInfo.getNetworkStatus();
        if (sessionId !== this.locationSessionId || !networkStatus.connected) {
          return;
        }
        const currentTimestamp = position.timestamp;
        if (currentTimestamp >= timestamp + GlobalConstants.GPS_TRACKING_POLLING_INTERVAL) {
          timestamp = currentTimestamp;
          await this.uploadCurrentLocation({
            PositionTimestamp: dayjs().format(),
            Latitude: position.coords.latitude,
            Longitude: position.coords.longitude,
            InputType: inputType,
          });
        }
      }
    });
  }

  private async uploadCurrentLocation(location: { PositionTimestamp: string; Latitude: number; Longitude: number; InputType: string }) {
    const user = JSON.parse(localStorage.getItem(GlobalConstants.USER_KEY));
    const uri = environment.mobileApiUrl + GlobalConstants.LOG_USER_POSITION_URI;

    const options = {
      url: uri,
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + user.AccessToken },
      data: location,
    };
  
    let data: LogUserPositionResponse = null;
    try {
      const response: HttpResponse = await CapacitorHttp.post(options);
      data = response.data;
    } catch (error) {
      console.error('HTTP POST failed', error);
    } 
    this.eventBusService.emit({ type: AppEventType.USER_CURRENT_POSITION_LOGGED, data });
  }
}
